import {
  DialogActionTrigger,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from '@/components/ui/dialog';
import { Box, Button, Heading, Link, List, Text, VStack } from '@chakra-ui/react';

import React from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle, OpenNewWindow } from 'iconoir-react';

interface ShopifyUninstallGuideDialogProps {
  isOpen: boolean;
  storeName?: string;
  shopDomain?: string;
  onClose: () => void;
}

const ShopifyUninstallGuideDialog: React.FC<ShopifyUninstallGuideDialogProps> = ({
  isOpen,
  storeName,
  shopDomain,
  onClose,
}) => {
  const { t } = useTranslation();

  const shopifyAdminUrl = shopDomain
    ? `https://${shopDomain}/admin/settings/apps`
    : 'https://admin.shopify.com/settings/apps';

  return (
    <DialogRoot open={isOpen} onOpenChange={onClose}>
      <DialogContent maxW="lg" rounded="xl">
        <DialogHeader>
          <Box>
            <DialogTitle>{t('integrations.shopify.uninstallGuide.title')}</DialogTitle>
            <DialogDescription>
              {t('integrations.shopify.uninstallGuide.description')}
            </DialogDescription>
          </Box>
        </DialogHeader>

        <DialogBody>
          <VStack gap={4} align="stretch">
            {/* Uninstall Steps */}
            <Box>
              <Heading as="h4" size="sm" mb={3}>
                {t('integrations.shopify.uninstallGuide.stepsTitle')}
              </Heading>
              <List.Root gap={3} variant="plain" align="start" fontSize="sm">
                <List.Item>
                  <List.Indicator asChild color="gray.600">
                    <Text fontWeight="bold">1.</Text>
                  </List.Indicator>
                  <VStack align="start" gap={1}>
                    <Text>{t('integrations.shopify.uninstallGuide.step1')}</Text>
                    <Link
                      href={shopifyAdminUrl}
                      color="blue.600"
                      fontSize="sm"
                      display="inline-flex"
                      alignItems="center"
                      gap={1}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t('integrations.shopify.uninstallGuide.openShopifyAdmin')}
                      <OpenNewWindow width={14} height={14} />
                    </Link>
                  </VStack>
                </List.Item>

                <List.Item>
                  <List.Indicator asChild color="gray.600">
                    <Text fontWeight="bold">2.</Text>
                  </List.Indicator>
                  <Text>{t('integrations.shopify.uninstallGuide.step2')}</Text>
                </List.Item>

                <List.Item>
                  <List.Indicator asChild color="gray.600">
                    <Text fontWeight="bold">3.</Text>
                  </List.Indicator>
                  <Text>{t('integrations.shopify.uninstallGuide.step3')}</Text>
                </List.Item>

                <List.Item>
                  <List.Indicator asChild color="gray.600">
                    <Text fontWeight="bold">4.</Text>
                  </List.Indicator>
                  <Text>{t('integrations.shopify.uninstallGuide.step4')}</Text>
                </List.Item>
              </List.Root>
            </Box>

            {/* What Happens After */}
            <Box p={3} bg="gray.50" rounded="md">
              <Heading as="h4" size="sm" mb={2}>
                {t('integrations.shopify.uninstallGuide.afterUninstall')}
              </Heading>
              <List.Root gap={2} variant="plain" align="center" fontSize="sm">
                <List.Item>
                  <List.Indicator asChild color="primary.500" boxSize={4}>
                    <CheckCircle />
                  </List.Indicator>
                  <Text>{t('integrations.shopify.uninstallGuide.after1')}</Text>
                </List.Item>
                <List.Item>
                  <List.Indicator asChild color="primary.500" boxSize={4}>
                    <CheckCircle />
                  </List.Indicator>
                  <Text>{t('integrations.shopify.uninstallGuide.after2')}</Text>
                </List.Item>
                <List.Item>
                  <List.Indicator asChild color="primary.500" boxSize={4}>
                    <CheckCircle />
                  </List.Indicator>
                  <Text>{t('integrations.shopify.uninstallGuide.after3')}</Text>
                </List.Item>
                <List.Item>
                  <List.Indicator asChild color="primary.500" boxSize={4}>
                    <CheckCircle />
                  </List.Indicator>
                  <Text>{t('integrations.shopify.uninstallGuide.after4')}</Text>
                </List.Item>
              </List.Root>
            </Box>
          </VStack>
        </DialogBody>

        <DialogFooter>
          <DialogActionTrigger asChild>
            <Button onClick={onClose}>{t('common.close')}</Button>
          </DialogActionTrigger>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
};

export default ShopifyUninstallGuideDialog;
